/**
 * TicketTimeline - Conversation history for tickets
 */

import { cn } from '@/lib/utils';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { Bot, HeadphonesIcon } from 'lucide-react';
import type { ReplySenderType, TicketReply } from '../types';

interface TicketTimelineProps {
  replies: TicketReply[];
  initialMessage?: string;
  initialDate?: string;
  className?: string;
}

export function TicketTimeline({
  replies,
  initialMessage,
  initialDate,
  className
}: TicketTimelineProps) {
  const getSenderIcon = (type: ReplySenderType) => {
    switch (type) {
      case 'user':
        return <SFIcon icon={sfIcons.user} className="h-4 w-4" />;
      case 'support':
        return <HeadphonesIcon className="h-4 w-4" />;
      case 'ai':
        return <Bot className="h-4 w-4" />;
      case 'system':
        return <SFIcon icon={sfIcons.settings} className="h-4 w-4" />;
    }
  };

  const getSenderLabel = (type: ReplySenderType) => {
    switch (type) {
      case 'user':
        return 'You';
      case 'support':
        return 'Support Team';
      case 'ai':
        return 'AI Assistant';
      case 'system':
        return 'System';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const allMessages: Array<{
    id: string;
    senderType: ReplySenderType;
    content: string;
    createdAt: string;
  }> = [];

  // Add initial message if provided
  if (initialMessage && initialDate) {
    allMessages.push({
      id: 'initial',
      senderType: 'user',
      content: initialMessage,
      createdAt: initialDate,
    });
  }

  // Add replies
  replies.forEach((reply) => {
    allMessages.push({
      id: reply.id,
      senderType: reply.sender_type,
      content: reply.content,
      createdAt: reply.created_at,
    });
  });

  if (allMessages.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {allMessages.map((message, index) => {
        const isUser = message.senderType === 'user';
        const isSystem = message.senderType === 'system';

        // System messages are different
        if (isSystem) {
          return (
            <div key={message.id} className="flex justify-center">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full">
                <SFIcon icon={sfIcons.settings} className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {message.content}
                </span>
              </div>
            </div>
          );
        }

        return (
          <div
            key={message.id}
            className={cn(
              'flex gap-3',
              isUser ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            {/* Avatar */}
            <div
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-full',
                'flex items-center justify-center',
                isUser
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              {getSenderIcon(message.senderType)}
            </div>

            {/* Message */}
            <div
              className={cn(
                'flex flex-col max-w-[80%]',
                isUser ? 'items-end' : 'items-start'
              )}
            >
              {/* Sender name */}
              <span className="text-xs text-muted-foreground mb-1">
                {getSenderLabel(message.senderType)} • {formatTime(message.createdAt)}
              </span>

              {/* Content */}
              <div
                className={cn(
                  'px-4 py-2.5 rounded-2xl',
                  isUser
                    ? ['bg-primary text-primary-foreground', 'rounded-br-md']
                    : ['bg-muted text-foreground', 'rounded-bl-md']
                )}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
